import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { BDF_SERVICE, BdfJob, BdfJobStatus, BdfService } from '@infinitform/platform-core';
import { InlineSpinnerComponent } from '../spinner/inline-spinner.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';

// s3Key (cloud) and localPath (desktop) are mutually exclusive — either signals
// a done file is present for the smartIndicator() check below.
type BdfPillJob = {
  status: string;
  s3Key?: string;
  localPath?: string;
  filename?: string;
  source?: 'organic' | 'prismatic' | 'analysis';
  progress?: number;
  stage?: string;
};

export interface BdfPillSource {
  id: string;
  bdfJob?: BdfPillJob | null;
  children?: Array<{
    id: string;
    bdfJob?: BdfPillJob | null;
  }>;
}

export type BdfPillSize = 'xxs' | 'xs' | 'sm';

// Single BDF status pill used everywhere (project lists, sharing lists, editor
// sidenav, modal child rows, modal single-project status). Two modes:
//   1. Smart:  pass [item] — pill auto-renders 'running' (with spinner) or
//              'done', stays empty otherwise. Used by list rows.
//   2. Direct: pass [status] (and optional [text]) — pill renders any of the
//              5 BDF states verbatim. Used inside the BDF modal.
@Component({
  selector: 'app-bdf-row-pill',
  templateUrl: './bdf-row-pill.component.html',
  styleUrls: ['./bdf-row-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InlineSpinnerComponent, TooltipDirective],
})
export class BdfRowPillComponent {
  private readonly bdfService = inject(BDF_SERVICE, { optional: true }) as BdfService | null;

  /** Smart mode: pill resolves status from this list-row record. */
  readonly item = input<BdfPillSource | null>(null);

  /** Direct mode: explicit BDF status to render. Wins over `item`. */
  readonly status = input<BdfJobStatus | null | undefined>(undefined);

  /** Pill text override; falls back to per-status default ("Running", "Done", …). */
  readonly text = input<string | null>(null);

  /** Visual size: 'xs' (default, 24px) for lists; 'sm' (32px) for modal single-project. */
  readonly size = input<BdfPillSize>('xs');

  /** Tooltip override; falls back to a per-status default. */
  readonly tooltip = input<string | null>(null);

  /**
   * In smart mode the pill renders only running/done (idle/queued/error are
   * suppressed because the list row has its own UI for those). In direct mode
   * the caller decides — set `showAll` true to render all five states.
   */
  readonly showAll = input<boolean>(false);

  readonly effectiveStatus = computed<BdfJobStatus | null>(() => {
    const direct = this.status();
    if (direct !== undefined) return direct ?? null;
    return this.smartIndicator();
  });

  readonly visible = computed<boolean>(() => {
    const s = this.effectiveStatus();
    if (!s) return false;
    if (this.showAll() || this.status() !== undefined) return true;
    return s === 'running' || s === 'done';
  });

  readonly effectiveText = computed<string>(() => {
    const override = this.text();
    if (override !== null) return override;
    return defaultLabel(this.effectiveStatus());
  });

  readonly effectiveTooltip = computed<string>(() => {
    return this.tooltip() ?? defaultTooltip(this.effectiveStatus());
  });

  /** Resolve a 'running' / 'done' indicator from the project-list-row record. */
  private smartIndicator(): BdfJobStatus | null {
    const it = this.item();
    if (!it) return null;
    const persistedJobs: Array<BdfJob | null> = it.children && it.children.length > 0
      ? it.children.map((c) => this.live(c.id, c.bdfJob))
      : [this.live(it.id, it.bdfJob)];
    const present = persistedJobs.filter((j): j is BdfJob => !!j);
    if (present.length === 0) return null;
    if (present.some((j) => j.status === 'queued' || j.status === 'running')) return 'running';
    if (present.every((j) => j.status === 'done' && (!!j.s3Key || !!j.localPath))) return 'done';
    return null;
  }

  private live(id: string, persisted: BdfPillSource['bdfJob']): BdfJob | null {
    return this.bdfService?.bdfStatusFor(id)() ?? (persisted as BdfJob | null) ?? null;
  }
}

function defaultLabel(status: BdfJobStatus | null): string {
  switch (status) {
    case 'running': return 'BDF';
    case 'done':    return 'BDF Ready';
    case 'queued':  return 'Queued';
    case 'error':   return 'Error';
    case 'idle':    return 'Not generated';
    default:        return '';
  }
}

function defaultTooltip(status: BdfJobStatus | null): string {
  switch (status) {
    case 'running': return 'BDF generation in progress';
    case 'done':    return 'BDF available';
    case 'queued':  return 'BDF queued';
    case 'error':   return 'BDF generation failed';
    case 'idle':    return 'BDF not generated yet';
    default:        return '';
  }
}
